import { useCallback, useEffect, useState } from "react";
import { Plus, Copy, Trash2, CheckCircle2, Clock, XCircle, Mail, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const INPUT = "w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary";

const STATUS = {
  open:    { Icon: Clock,        cls: "text-muted-foreground", label: "Open" },
  used:    { Icon: CheckCircle2, cls: "text-positive",         label: "Gebruikt" },
  expired: { Icon: XCircle,      cls: "text-destructive",      label: "Verlopen" },
};

export default function DiscountCodesBlock() {
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState({ email: "", percent: 10, applies_to: "first", expiry_days: 30, note: "" });
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mailingId, setMailingId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/discounts"); setCodes(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setCodes([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/discounts", {
        ...form, percent: Number(form.percent), expiry_days: Number(form.expiry_days), email: form.email.trim(), send_email: sendEmail,
      });
      if (data.email_sent_at) toast.success(`Code ${data.code} aangemaakt en gemaild naar ${data.email}`);
      else if (data.email_error) toast.warning(`Code ${data.code} aangemaakt, maar niet gemaild: ${data.email_error}`);
      else toast.success(`Code ${data.code} aangemaakt voor ${data.email}`);
      setForm({ email: "", percent: 10, applies_to: "first", expiry_days: 30, note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Aanmaken mislukt.");
    }
    setBusy(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Kortingscode ${c.code} verwijderen?`)) return;
    try { await api.delete(`/admin/discounts/${c.id}`); toast.success("Verwijderd"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resend = async (c) => {
    setMailingId(c.id);
    try {
      await api.post(`/admin/discounts/${c.id}/send`);
      toast.success(`Code ${c.code} gemaild naar ${c.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Mailen mislukt."); }
    setMailingId(null);
  };

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Code gekopieerd"); }
    catch { toast.error("Kopiëren mislukt — kopieer handmatig"); }
  };

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow overflow-x-auto" data-testid="discount-codes-block">
      <div className="p-5 border-b-2 border-foreground">
        <h2 className="font-display font-black text-2xl uppercase tracking-tight">Kortingscodes</h2>
        <p className="text-xs text-muted-foreground mt-1">Eén code per klant (gekoppeld aan e-mailadres), eenmalig te gebruiken op de prijzenpagina.</p>
      </div>
      <form onSubmit={create} className="p-5 border-b-2 border-foreground grid gap-3 md:grid-cols-5" data-testid="discount-create-form">
        <div className="md:col-span-2">
          <label className="stat-label">E-mailadres klant</label>
          <input type="email" required value={form.email} onChange={set("email")} placeholder="penningmeester@club.nl" data-testid="discount-email-input" className={INPUT} />
        </div>
        <div>
          <label className="stat-label">Korting %</label>
          <input type="number" min="1" max="90" required value={form.percent} onChange={set("percent")} data-testid="discount-percent-input" className={`${INPUT} font-mono-num`} />
        </div>
        <div>
          <label className="stat-label">Geldt voor</label>
          <select value={form.applies_to} onChange={set("applies_to")} data-testid="discount-applies-select" className={INPUT}>
            <option value="first">Alleen eerste jaar</option>
            <option value="recurring">Ook op verlenging</option>
          </select>
        </div>
        <div>
          <label className="stat-label">Verloopt na (dagen)</label>
          <input type="number" min="0" max="365" value={form.expiry_days} onChange={set("expiry_days")} data-testid="discount-expiry-input" className={`${INPUT} font-mono-num`} />
        </div>
        <div className="md:col-span-4">
          <label className="stat-label">Notitie (optioneel)</label>
          <input value={form.note} onChange={set("note")} placeholder="bv. actie voorjaar 2026" data-testid="discount-note-input" className={INPUT} />
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={busy} data-testid="discount-create-btn"
            className="btn-brutal bg-primary text-primary-foreground w-full px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Plus className="w-4 h-4" /> {sendEmail ? "Aanmaken + mailen" : "Code aanmaken"}
          </button>
        </div>
        <label className="md:col-span-5 inline-flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
            data-testid="discount-send-email-checkbox" className="accent-foreground w-4 h-4" />
          <Mail className="w-3.5 h-3.5" /> Mail de code direct naar de klant, met link naar de prijzenpagina
        </label>
        <p className="md:col-span-5 text-[10px] text-muted-foreground">Maximaal 90% — een betaling van € 0 kan Mollie niet verwerken. 0 dagen = geen vervaldatum.</p>
      </form>
      {codes === null ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div>
      ) : codes.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="discount-codes-empty">Nog geen kortingscodes.</div>
      ) : (
        <table className="w-full text-sm" data-testid="discount-codes-table">
          <thead>
            <tr className="bg-foreground text-background">
              {["Code", "Klant", "Korting", "Geldt voor", "Verloopt", "Status", "Acties"].map((h) => (
                <th key={h} className={`px-3 py-2 font-display uppercase text-xs tracking-widest ${h === "Acties" ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => {
              const { Icon, cls, label } = STATUS[c.status] || STATUS.open;
              return (
                <tr key={c.id} data-testid={`discount-row-${c.code}`} className={c.status === "used" ? "bg-muted/40" : c.status === "expired" ? "bg-destructive/5" : ""}>
                  <td className="px-3 py-3 font-mono-num font-bold tracking-widest">{c.code}</td>
                  <td className="px-3 py-3">
                    <div className="font-mono-num text-xs">{c.email}</div>
                    {c.email_sent_at && (
                      <div className="inline-flex items-center gap-1 text-positive text-[10px] mt-0.5" data-testid={`discount-mailed-${c.code}`}>
                        <Mail className="w-3 h-3" /> Gemaild {fmtDate(c.email_sent_at)}
                      </div>
                    )}
                    {c.note && <div className="text-xs text-muted-foreground italic">{c.note}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono-num font-bold">{c.percent}%</td>
                  <td className="px-3 py-3 text-xs">{c.applies_to === "recurring" ? "Elk jaar" : "Eerste jaar"}</td>
                  <td className="px-3 py-3 font-mono-num text-xs">{fmtDate(c.expires_at)}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold ${cls}`}><Icon className="w-3.5 h-3.5" /> {label}</span>
                    {c.used_at && <div className="text-[10px] text-muted-foreground">{fmtDate(c.used_at)}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2 justify-end">
                      {c.status === "open" && (
                        <button onClick={() => resend(c)} disabled={mailingId === c.id} data-testid={`discount-resend-${c.code}`}
                          className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                          {mailingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                          {c.email_sent_at ? "Mail opnieuw" : "Mail code"}
                        </button>
                      )}
                      <button onClick={() => copy(c.code)} data-testid={`discount-copy-${c.code}`}
                        className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
                        <Copy className="w-3.5 h-3.5" /> Kopieer
                      </button>
                      {c.status !== "used" && (
                        <button onClick={() => remove(c)} data-testid={`discount-delete-${c.code}`}
                          className="btn-brutal bg-destructive text-white px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
